import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import { formatPrice, convertGQLToProductJson, handleize } from 'utils/general';
import debounce from 'lodash/debounce';
import { i18n } from 'lib/i18n.js';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import { addToCartEventData } from 'lib/tracking';
import datalayer from 'lib/datalayer';

export default {
  components: {
    'quantity-selector': () =>
      import('../quantity-selector/quantity-selector.vue'),
    'vue-image': () => import('../vue-image/vue-image.vue'),
  },
  i18n,
  name: 'header-cart-item',
  props: {
    single: {
      type: Object,
    },
    cartPage: {
      type: [Object, Number],
    },
    isChild: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: false,
      showQTY: true,
      insoleVariant: false,
      hasMicroCollection: false,
    };
  },
  watch: {
    single(_new, _old) {
      if (_old.quantity !== _new.quantity) {
        this.showQTY = false;
        setTimeout(() => {
          this.showQTY = true;
        }, 100);
      }
    },
  },
  computed: {
    ...mapState('global', ['cart', 'isMobile']),
    ...mapState('cart', ['cart_insoles', 'loading']),
    option() {
      return `${this.single.options_with_values[0].name}: ${this.single.options_with_values[0].value}`;
    },
    maximumText() {
      return this.$t('cart.label.maximum', {
        count: this.maxQuantity,
      });
    },
    removeText() {
      return this.$t('cart.gwp.remove');
    },
    editText() {
      return this.$t('cart.gwp.edit');
    },
    isBundle() {
      return this.single.isBundle ?? false;
    },
    isGWP() {
      return this.single.properties?._gwp;
    },
    computedQuantity() {
      return this.single.quantity;
    },
    productTitle() {
      return this.single.product_title.split(
        window.__GLOBAL__.productDelimiter,
      )[0];
    },
    hasMicro() {
      return this.hasMicroCollection;
    },
    maxQuantity() {
      const maxQuantityProp =
        this.single.properties &&
        Object.keys(this.single.properties).find(
          (key) => key === `_${Shopify.country.toUpperCase()}`,
        );
      return this.single.properties?.[maxQuantityProp] || 5;
    },
  },
  async beforeMount() {
    const baseTitle = handleize(this.productTitle);
    const hasMicroCollection = await this.checkMicroCollection(baseTitle);
    this.hasMicroCollection = hasMicroCollection;
  },
  methods: {
    ...mapActions('cart', ['updateBundleCartItems', 'updateCartLine']),
    ...mapActions('global', ['openModal']),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
      'updateItemToUpdate',
      'updateAction',
      'checkMicroCollection',
    ]),
    updateQuantity: debounce(function (quantity) {
      let currentQuantity = 0;

      for (let i = 0; i < this.cart.items.length; i++) {
        const cartItem = this.cart.items[i];
        if (cartItem.id === this.single.id) {
          currentQuantity = cartItem.quantity;
          if (
            cartItem.properties !== null &&
            cartItem.properties._bundle !== 'undefined'
          ) {
            this.single.isBundle = true;
          }
          break;
        }
      }

      if (this.isBundle) {
        this.updateBundleCartItems({
          quantity,
          items: this.single.items,
        });
      } else {
        this.updateCartLine({
          quantity,
          id: this.single.key,
        });
      }

      const extraData = {
        addToCartType: this.getAddToCartType(),
        sku:
          typeof this.single.sku !== 'undefined'
            ? this.single.sku
            : this.single.id,
      };

      let action = 'addToCart';
      if (currentQuantity > quantity) {
        action = 'removeFromCart';
      }

      if (currentQuantity < quantity) {
        datalayer.dataLayerPush(
          action,
          addToCartEventData(this.single, quantity, extraData),
        );
      }
    }, 450),
    holdQuantity() {},
    getAddToCartType() {
      let addToCartType = 'Home';
      if (window.location.pathname.indexOf('/collections') > -1) {
        addToCartType = 'Product List Page';
        if (window.location.pathname.indexOf('/products') > -1) {
          addToCartType = 'Product Detail Page';
        }
      } else if (window.location.pathname.indexOf('/cart') > -1) {
        addToCartType = 'Cart';
      } else if (window.location.pathname.indexOf('/products') > -1) {
        addToCartType = 'Product Detail Page';
      } else if (window.location.pathname.indexOf('/pages') > -1) {
        addToCartType = 'Landing Page';
      } else if (window.location.pathname.indexOf('/account') > -1) {
        addToCartType = 'Account Page';
      }

      return addToCartType;
    },
    onRemove() {
      if (this.isBundle) {
        this.removeBundle();
      } else {
        this.updateCartLine({
          quantity: 0,
          id: this.single.key,
        });
        this.removeAltProductToList(this.single.key);
      }

      const extraData = {
        addToCartType: this.getAddToCartType(),
        sku:
          typeof this.single.sku !== 'undefined'
            ? this.single.sku
            : this.single.id,
      };
      this.single.isBundle = this.isBundle;
      datalayer.dataLayerPush(
        'removeFromCart',
        addToCartEventData(this.single, 0, extraData),
      );
    },
    removeBundle() {
      this.updateBundleCartItems({
        quantity: 0,
        items: this.single.items,
      });
    },
    async edit(lineItem) {
      const storefrontResponse = await storefront.request(gqlQueries.product, {
        handle: lineItem.handle,
      });
      const product = convertGQLToProductJson(storefrontResponse.product);

      this.updateAction('edit');
      this.updateItemToUpdate(lineItem.id);
      this.updateModalTitle(this.$t('quick_view.edit_item_title'));

      const ctaText = this.isMobile
        ? this.$t('quick_view.update')
        : this.$t('quick_view.update_my_bag');
      this.updateCtaText(ctaText);
      this.setProduct(product);
      this.openModal('quick-view');
    },
    priceFormat(price, freeIfZero = true) {
      return price > 0 || !freeIfZero
        ? formatPrice(price, false, true)
        : this.$t('cart.general.free');
    },
  },
  template: `
      <div class="header-cart-item" :class="{'is-bundle':isBundle, 'is-child':isChild}">
        <div class="w1 relative f jcb">
          <a
            class="header-cart__favorite-btn icon-btn icon-btn--favorite-simple"
            aria-label="Click Here"
            title="Click Here"
            tabindex="0"
            data-module-name="header cart item"
            >
            <div class="icon-btn__icon">
              <svg width="34" height="30" viewBox="0 0 34 30">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-favorite-simple" x="0" y="0"></use>
              </svg>
            </div>
            <div class="icon-btn__icon--hover">
              <svg width="34" height="30" viewBox="0 0 34 30">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-favorite-simple-red" x="0" y="0"></use>
              </svg>
            </div>
          </a>
          <div class="header-cart__element__image relative" v-if="!isBundle">
            <div
              is="vue-image"
              :src="single.featured_image.url"
              :size="100"
              :alt="single.product_title"
              spinner-size="sm"
              css="fit-cover">
            </div>
          </div>
          <div class="header-cart__element__data">
            <div class="relative fg">
              <div
                class="header-cart__element__title p1 bold uppercase"
                v-text="productTitle">
              </div>
              <div
                v-if="!isChild"
                class="header-cart__element__price OneLinkNoTx p1"
              >
                <div class="p1 align-r">
                  <span v-if="single.compare_at_price && single.compare_at_price !== 'null'">
                    <span class="discounted" v-if="parseInt(single.compare_at_price) > parseInt(single.price)" v-text="priceFormat(single.compare_at_price, false)"/>
                    <span class="bold red" v-text="priceFormat(single.price)"/>
                  </span>
                  <span v-else>
                    <span class="grey" v-text="priceFormat(single.price)"/>
                  </span>
                </div>
                <div class="p1 bold align-r" v-text="priceFormat(single.line_price)"></div>
              </div>
              <div class="header-cart__element__option" v-if="!isBundle && !isChild">
                <span class="p2" v-if="single.variant_title" v-text="single.options_with_values[0].value"></span>
                <a v-if="hasMicro" href="javascript:void(0)" class="p2 header-cart__edit" @click="edit(single)" v-text="editText"></a>
              </div>
              <div class="relative header-cart__qty" v-if="!isChild" :class="{'header-cart__qty--loading': (loading === single.id)}">
                <div class="f aic">
                  <div
                    is="quantity-selector"
                    v-if="showQTY && !isGWP"
                    @change="updateQuantity"
                    :value="computedQuantity"
                    :min="0"
                    :max="maxQuantity"
                    :small="true"
                    ></div>
                  <div
                    is="quantity-selector"
                    v-else
                    @change="holdQuantity"
                    :value="computedQuantity"
                    :min="0"
                    :max="maxQuantity"
                    :small="true"
                    ></div>
                  <p v-if="computedQuantity >= maxQuantity" v-text="maximumText" class="header-cart__element__maximun p2 red"></p>
                  <button type="button" class="p2 header-cart__remove" @click="onRemove" v-text="removeText"></button>
                </div>
                <p v-if="computedQuantity >= maxQuantity" v-text="maximumText" class="header-cart__element__maximun header-cart__element__maximun-mo p2 red"></p>
              </div>
            </div>
            <div v-if="isBundle" class="header-cart-item__bundle__items">
              <span class="p2"><span class="b1">Includes {{single.items.length}} items:</span></span>
              <div class="header-cart-item__bundle__items-wrapper">
                <header-cart-item v-for="item in single.items"
                  :key="single.id+'_'+item.id+'_'+item.properties._bundle_index"
                  :single="item"
                  :cartPage="cartPage"
                  :isChild="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      `,
};
